import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Dropdown } from '@/components/Global'
import { DropdownType } from '@/enums'
import { AssignmentType } from "../AssessmentModal/AssessmentModal";
import './TestingWindow.scss';
import { AssessmentWindow } from '@/features/dashboard/utils/findAssessmentWindow';
import { useAssessmentPeriodWindows } from "@/hooks/useAssessmentPeriods";
import { AssessmentPeriodItem } from '@/hooks/useAssessmentPeriods';
import { AssessmentPeriodWindowType } from '@/hooks/useAssessmentPeriods';
import moment from 'moment';

export interface SelectedPeriodOption {
  label: string;
  value: string;
  startDate: string;
  endDate: string;
  originalPeriodId: number;
  screeningWindowType: string;
  assignmentId: string | null;
  assignmentType: AssignmentType | null;
  filterType: 'BENCHMARK' | 'PROGRESS_MONITOR' | 'PERIOD';
  benchmarkTag?: 'BOY' | 'MOY' | 'EOY';
}

export interface SelectedPeriodCallback {
  label: string;
  value: number;
  startDate: string;
  endDate: string;
  screeningWindowType: string;
  assignmentId: string | null;
  assignmentType: AssignmentType | null;
  filterType: 'BENCHMARK' | 'PROGRESS_MONITOR' | 'PERIOD';
  benchmarkTag?: 'BOY' | 'MOY' | 'EOY';
}

export interface TestingWindowProps {
  assessmentModal: boolean;
  assessmentWindowStatus?: AssessmentWindow;
  onSelectedPeriodChange?: (selectedOption: SelectedPeriodCallback) => void;
  currentDistrictWindow?: AssessmentPeriodItem;
  controlledSelectedWindow?: { 
    startDate: string | null; 
    endDate: string | null; 
    value: number | null; 
    screeningWindowType: string | null; 
    assignmentId: string | null;
    filterType?: 'BENCHMARK' | 'PROGRESS_MONITOR' | 'PERIOD';
    benchmarkTag?: 'BOY' | 'MOY' | 'EOY';
  };
}

export const formatPeriodDateWithTags = (startDate: string, endDate: string, assignmentType: string, current?: boolean, screeningWindowType?: string, tags?: string, outOfDistrictWindow?: boolean, periodId?: number) => {
  // Match the same logic as generateOptions() for consistency
  
  // For benchmark assignments - ONLY show if they have BOY/MOY/EOY tags
  if ((screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || 
       screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT) &&
      assignmentType === 'BENCHMARK') {
    
    // Must have tags
    if (!tags) {
      return null; // Don't show benchmarks without tags
    }
    
    const tagsArray = Array.isArray(tags) ? tags : [tags];
    
    if (tagsArray.includes('BOY') || tagsArray.includes('WINDOW_BOY')) {
      return 'BOY Benchmark';
    } else if (tagsArray.includes('MOY') || tagsArray.includes('WINDOW_MOY')) {
      return 'MOY Benchmark';
    } else if (tagsArray.includes('EOY') || tagsArray.includes('WINDOW_EOY')) {
      return 'EOY Benchmark';
    } else {
      // Benchmark without valid BOY/MOY/EOY tags - don't show
      return null;
    }
  }
  
  // For period assignments (progress monitoring)
  if (screeningWindowType === AssessmentPeriodWindowType.PERIOD && periodId) {
    return `Period ${periodId}`;
  }
  
  // Fallback for other assignment types (not benchmarks)
  const formattedName = assignmentType
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return formattedName;
};

const TestingWindow = ({ assessmentModal, onSelectedPeriodChange, currentDistrictWindow, controlledSelectedWindow, assessmentWindowStatus }: TestingWindowProps) => {
  const { loading, assessmentPeriodWindows } = useAssessmentPeriodWindows();

  const generateOptions = useCallback(() => {
    const options: SelectedPeriodOption[] = [];
    const today = moment.utc();

    // 1. Generate BOY/MOY/EOY Benchmark options (include both district and school assignments)
    const benchmarkAssignments = assessmentPeriodWindows.filter(period => {
      // Must be a district or school assignment
      if (!(period.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || 
            period.screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT)) {
        return false;
      }
      
      // Must be a benchmark assignment
      if (period.assignmentType !== 'BENCHMARK') {
        return false;
      }
      
      // Must have tags
      if (!period.tags) {
        return false;
      }
      
      // Tags must include BOY/MOY/EOY
      const tagsArray = Array.isArray(period.tags) ? period.tags : [period.tags];
      const hasValidTag = ['BOY', 'WINDOW_BOY', 'MOY', 'WINDOW_MOY', 'EOY', 'WINDOW_EOY'].some(tag => 
        tagsArray.includes(tag)
      );
      
      if (!hasValidTag) {
        return false;
      }
      
      return true;
    });

    benchmarkAssignments.forEach(assignment => {
      const startDate = moment.utc(assignment.startDate);
      const daysUntilStart = startDate.diff(today, 'days');
      
      // Only show if we're within 7 days of start date (or already started)
      if (daysUntilStart <= 7) {
        let benchmarkTag: 'BOY' | 'MOY' | 'EOY';
        let label: string;
        
        const tagsArray = Array.isArray(assignment.tags) ? assignment.tags : [assignment.tags];
        
        if (tagsArray.includes('BOY') || tagsArray.includes('WINDOW_BOY')) {
          benchmarkTag = 'BOY';
          label = 'BOY Benchmark';
        } else if (tagsArray.includes('MOY') || tagsArray.includes('WINDOW_MOY')) {
          benchmarkTag = 'MOY';
          label = 'MOY Benchmark';
        } else if (tagsArray.includes('EOY') || tagsArray.includes('WINDOW_EOY')) {
          benchmarkTag = 'EOY';
          label = 'EOY Benchmark';
        } else {
          return; // Skip if no recognized tag
        }

        // Check if this is the current district window
        const isCurrentBenchmark = currentDistrictWindow && 
                                  currentDistrictWindow.assignmentType === 'BENCHMARK' &&
                                  currentDistrictWindow.periodId === assignment.periodId &&
                                  currentDistrictWindow.screeningWindowType === assignment.screeningWindowType;
        
        const finalLabel = isCurrentBenchmark ? `${label} (current)` : label;

        const option = {
          label: finalLabel,
          value: `benchmark-${benchmarkTag}-${assignment.periodId}`,
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          originalPeriodId: assignment.periodId,
          screeningWindowType: assignment.screeningWindowType,
          assignmentId: assignment.assignmentId,
          assignmentType: 'BENCHMARK' as AssignmentType,
          filterType: 'BENCHMARK' as const,
          benchmarkTag
        };
        
        options.push(option);
      }
    });

    // 2. Generate Period 1-12 options with dynamic behavior based on district windows
    const periodAssignments = assessmentPeriodWindows.filter(period => 
      period.screeningWindowType === AssessmentPeriodWindowType.PERIOD
    );

    // Get district progress monitoring assignments for reference
    const districtProgressMonitoringAssignments = assessmentPeriodWindows.filter(period => {
      return (period.screeningWindowType === AssessmentPeriodWindowType.DISTRICT_ASSIGNMENT || 
              period.screeningWindowType === AssessmentPeriodWindowType.SCHOOL_ASSIGNMENT) &&
             period.assignmentType === 'PROGRESS_MONITORING';
    });

    // Sort periods by periodId to ensure correct order
    periodAssignments
      .sort((a, b) => a.periodId - b.periodId)
      .forEach(period => {
        const periodStart = moment.utc(period.startDate);
        
        // Only show periods that have started (current or past)
        if (today.isSameOrAfter(periodStart, 'day')) {
          // Check if there's an active district PM window for this period
          const districtPMForThisPeriod = districtProgressMonitoringAssignments.find(dm => {
            const dmStartDate = moment.utc(dm.startDate);
            const daysUntilDMStart = dmStartDate.diff(today, 'days');
            return dm.periodId === period.periodId && daysUntilDMStart <= 7; // Within 7 days or started
          });

          // Determine filterType and screeningWindowType based on district window presence
          const hasActiveDistrictPM = !!districtPMForThisPeriod;
          
          // Check if this is the current period
          // Don't mark periods as current if there's an active assignment window (benchmark or PM)
          const hasActiveAssignmentWindow = assessmentWindowStatus?.activeDistrictWindow;
          const isCurrentPeriod = (hasActiveDistrictPM && currentDistrictWindow && 
                                  currentDistrictWindow.assignmentType === 'PROGRESS_MONITORING' &&
                                  currentDistrictWindow.periodId === period.periodId) ||
                                 (!hasActiveDistrictPM && !hasActiveAssignmentWindow && assessmentWindowStatus?.currentPeriodIdentifier === period.periodId);
          
          const periodLabel = isCurrentPeriod ? `Period ${period.periodId} (current)` : `Period ${period.periodId}`;

          const option = {
            label: periodLabel,
            value: hasActiveDistrictPM ? `district-progress-${period.periodId}` : `period-${period.periodId}`,
            startDate: period.startDate,
            endDate: period.endDate,
            originalPeriodId: period.periodId,
            screeningWindowType: hasActiveDistrictPM ? 'DistrictAssignment' : 'Period',
            assignmentId: hasActiveDistrictPM ? districtPMForThisPeriod.assignmentId : null,
            assignmentType: 'PROGRESS_MONITORING' as AssignmentType,
            filterType: hasActiveDistrictPM ? ('PROGRESS_MONITOR' as const) : ('PERIOD' as const)
          };
          
          options.push(option);
        }
      });

    return options;
  }, [assessmentPeriodWindows]);

  const options = useMemo(() => generateOptions(), [generateOptions]);

  const [selectedPeriod, setSelectedPeriod] = useState<SelectedPeriodOption | null>(null);

  const getTargetPeriod = useCallback(() => {
    if (loading || !assessmentPeriodWindows?.length || options.length === 0) {
      return selectedPeriod;
    }

    // If we have a controlled selection, find matching option
    if (controlledSelectedWindow?.value !== null && controlledSelectedWindow?.value !== undefined) {
      const found = options.find(option => {
        const matches = 
          controlledSelectedWindow.filterType === 'BENCHMARK' 
            ? (option.filterType === 'BENCHMARK' && 
               option.benchmarkTag === controlledSelectedWindow.benchmarkTag &&
               option.originalPeriodId === controlledSelectedWindow.value)
            : controlledSelectedWindow.filterType === 'PROGRESS_MONITOR'
            ? (option.filterType === 'PROGRESS_MONITOR' && 
               option.originalPeriodId === controlledSelectedWindow.value)
            : (option.filterType === 'PERIOD' && 
               option.originalPeriodId === controlledSelectedWindow.value);
        
        return matches;
      });
      
      if (found) {
        return found;
      }
    }

    // Check for current district/school benchmark window    
    if (currentDistrictWindow && currentDistrictWindow.assignmentType === 'BENCHMARK') {
      const found = options.find(option =>
        option.filterType === 'BENCHMARK' &&
        option.originalPeriodId === currentDistrictWindow.periodId &&
        (option.screeningWindowType === currentDistrictWindow.screeningWindowType)
      );
      if (found) return found;
    }

    // Check for current district/school progress monitoring window FIRST
    if (currentDistrictWindow && currentDistrictWindow.assignmentType === 'PROGRESS_MONITORING') {
      const found = options.find(option =>
        option.filterType === 'PROGRESS_MONITOR' &&
        option.originalPeriodId === currentDistrictWindow.periodId &&
        option.screeningWindowType === currentDistrictWindow.screeningWindowType
      );
      if (found) {
        return found;
      }
    }

    // Default to current period if available
    const currentPeriodOption = options.find(option => 
      option.filterType === 'PERIOD' && 
      option.originalPeriodId === assessmentWindowStatus?.currentPeriodIdentifier
    );
    
    if (currentPeriodOption) return currentPeriodOption;

    // Return first available option
    return options[0] || selectedPeriod;
  }, [loading, assessmentPeriodWindows, controlledSelectedWindow, currentDistrictWindow, options, assessmentWindowStatus, selectedPeriod]);

  useEffect(() => {
    const targetPeriod = getTargetPeriod();

    if (JSON.stringify(selectedPeriod) !== JSON.stringify(targetPeriod)) {
      setSelectedPeriod(targetPeriod as SelectedPeriodOption);
      
      if (targetPeriod && onSelectedPeriodChange) {
        onSelectedPeriodChange({
          label: targetPeriod.label,
          value: targetPeriod.originalPeriodId,
          startDate: targetPeriod.startDate,
          endDate: targetPeriod.endDate,
          screeningWindowType: targetPeriod.screeningWindowType,
          assignmentId: targetPeriod.assignmentId,
          assignmentType: targetPeriod.assignmentType as AssignmentType,
          filterType: targetPeriod.filterType,
          benchmarkTag: targetPeriod.benchmarkTag
        });
      }
    }
  }, [getTargetPeriod, selectedPeriod, onSelectedPeriodChange]);

  const handleDropdownChange = useCallback((selectedOption: SelectedPeriodOption) => {
    if (JSON.stringify(selectedOption) !== JSON.stringify(selectedPeriod)) {
      setSelectedPeriod(selectedOption);
      onSelectedPeriodChange?.({
        label: selectedOption.label,
        value: selectedOption.originalPeriodId,
        startDate: selectedOption.startDate,
        endDate: selectedOption.endDate,
        screeningWindowType: selectedOption.screeningWindowType,
        assignmentId: selectedOption.assignmentId,
        assignmentType: selectedOption.assignmentType as AssignmentType,
        filterType: selectedOption.filterType,
        benchmarkTag: selectedOption.benchmarkTag
      });
    }
  }, [onSelectedPeriodChange, selectedPeriod]);

  return (
    <div className={`testing-window d-flex ${assessmentModal ? 'flex-row align-items-center' : 'flex-column align-items-start align-self-start mb-3'} gap-2`}>
      <div className="d-flex align-items-center gap-2">
        <span className="testing-window-label">Testing Window</span>
      </div>
      <div>
        {(assessmentModal && options.length === 0) ? (
          <div className="testing-window-placeholder">
            <span>None</span>
          </div>
        ) : (
          <Dropdown
            styles={{ width: '18rem', zIndex: 10, fontSize: '0.8125rem', textOverflow: 'ellipsis', overflow: 'hidden', minHeight: '2.375rem', gap: '0.25rem' }}
            type={DropdownType.PRIMARY}
            placeholder={selectedPeriod?.label || 'Select Period'}
            isDisabled={assessmentModal}
            value={selectedPeriod}
            onChange={handleDropdownChange}
            aria-label="Select Testing Window"
            options={options}
          />
        )}
      </div>
    </div>
  );
};

export default TestingWindow;